import { Badge, Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { DEV_SERVER } from "../../../constants/urls";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import axios from "axios";
import { userSignal } from "../../../signal/userSignal";
import * as moment from "moment";
import DataTable from "react-data-table-component";
import avatar1 from "../../../assets/images/avatars/01.png";
import { useLocation, useNavigate } from "react-router-dom";

const STAGE_OPTIONS = ["Pending", "Approved", "Rejected"];

const STAGE_COLORS = {
    Pending: "warning",
    Approved: "success",
    Rejected: "danger",
};

export default function SuggestionDetails() {
    const location = useLocation();
    const navigate = useNavigate();
    const id = location.pathname.split("/")[3];

    const [suggestion, setSuggestion] = useState(null);

    const getSuggestion = useQuery(
        ["SuggestionDetails", id],
        async () => {
            const resp = await axios.get(`${DEV_SERVER}/suggestion/v2/${id}`, {
                headers: { Authorization: `Bearer ${userSignal.value?.token}` },
            });
            return resp.data;
        },
        {
            enabled: false,
            onSuccess: (data) => {
                if (data.status) {
                    setSuggestion(data.data);
                } else {
                    toast.error(data.message);
                }
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || "Something went wrong");
                navigate("/dashboard/suggestions");
            },
        }
    );

    const updateStageMutation = useMutation(
        (newStage) =>
            axios.put(
                `${DEV_SERVER}/suggestion/v2/${id}`,
                { currentStage: newStage },
                { headers: { Authorization: `Bearer ${userSignal.value?.token}` } }
            ),
        {
            onSuccess: (resp) => {
                if (resp.data.status) {
                    toast.success("Stage updated successfully");
                    setSuggestion((prev) => ({
                        ...prev,
                        currentStage: resp.data.data.currentStage,
                    }));
                } else {
                    toast.error(resp.data.message);
                }
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || "Failed to update stage");
            },
        }
    );

    useEffect(() => {
        getSuggestion.refetch({ cancelRefetch: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const itemsColumns = [
        {
            name: "#",
            center: true,
            width: "60px",
            selector: (_, idx) => idx + 1,
        },
        {
            name: "Product",
            center: true,
            selector: (row) => {
                const product = suggestion?.products?.[row?.productId?.toString()];
                return product?.name || row?.productId?.toString() || "—";
            },
        },
        {
            name: "Quantity",
            center: true,
            selector: (row) => row?.quantity,
        },
        {
            name: "Price",
            center: true,
            selector: (row) => `₹ ${parseFloat(row?.price || 0).toFixed(2)}`,
        },
        {
            name: "Subtotal",
            center: true,
            selector: (row) =>
                `₹ ${(parseFloat(row?.price || 0) * parseInt(row?.quantity || 0)).toFixed(2)}`,
        },
        {
            name: "Note",
            center: true,
            selector: (row) => {
                if (!row?.note) return "—";
                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip style={{ position: "fixed" }}>{row.note}</Tooltip>}
                    >
                        <span>{row.note}</span>
                    </OverlayTrigger>
                );
            },
        },
    ];

    if (getSuggestion.isLoading || getSuggestion.isFetching) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!suggestion) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
                <p className="text-muted">Suggestion not found.</p>
            </div>
        );
    }

    return (
        <Row>
            {/* Header */}
            <Col lg="12" style={{ marginTop: "3rem" }}>
                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center" style={{ columnGap: "0.75rem" }}>
                            <button
                                className="btn btn-light btn-sm"
                                onClick={() => navigate(-1)}
                            >
                                <i className="fas fa-arrow-left"></i>
                            </button>
                            <h4 className="card-title mb-0">Suggestion Details</h4>
                        </div>
                        <div className="d-flex align-items-center" style={{ columnGap: "0.75rem" }}>
                            <label className="mb-0 fw-semibold">Stage:</label>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: "auto" }}
                                value={suggestion?.currentStage || "Pending"}
                                disabled={updateStageMutation.isLoading}
                                onChange={(e) => updateStageMutation.mutate(e.target.value)}
                            >
                                {STAGE_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                            <Badge bg={STAGE_COLORS[suggestion?.currentStage] || "secondary"}>
                                {suggestion?.currentStage}
                            </Badge>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            {/* Farmer Info */}
                            <Col md="4" className="mb-3">
                                <h6 className="text-muted mb-1">Farmer</h6>
                                <p className="mb-0 fw-semibold">{suggestion?.farmer?.name || "—"}</p>
                                {suggestion?.farmer?.phoneNumber && (
                                    <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
                                        {suggestion.farmer.phoneNumber}
                                    </p>
                                )}
                                {suggestion?.farmer?.city && (
                                    <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
                                        {[suggestion.farmer.city, suggestion.farmer.state]
                                            .filter(Boolean)
                                            .join(", ")}
                                    </p>
                                )}
                            </Col>

                            {/* Added By */}
                            <Col md="4" className="mb-3">
                                <h6 className="text-muted mb-1">Added By</h6>
                                {suggestion?.user ? (
                                    <div className="d-flex align-items-center" style={{ columnGap: "0.5rem" }}>
                                        <img
                                            style={{ width: "32px", height: "32px", borderRadius: "50%" }}
                                            src={suggestion.user.profileURL || avatar1}
                                            alt=""
                                        />
                                        <div>
                                            <p className="mb-0 fw-semibold">{suggestion.user.name}</p>
                                            {suggestion.user.phone && (
                                                <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
                                                    {suggestion.user.phone}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="mb-0">—</p>
                                )}
                            </Col>

                            {/* Meta */}
                            <Col md="4" className="mb-3">
                                <h6 className="text-muted mb-1">Summary</h6>
                                <p className="mb-1">
                                    <span className="text-muted">Total Amount: </span>
                                    <strong>₹ {parseFloat(suggestion?.totalAmount || 0).toFixed(2)}</strong>
                                </p>
                                <p className="mb-1">
                                    <span className="text-muted">Items: </span>
                                    <strong>{suggestion?.suggestionItems?.length || 0}</strong>
                                </p>
                                <p className="mb-1">
                                    <span className="text-muted">Created: </span>
                                    {moment(suggestion?.createdAt).format("DD/MM/YYYY hh:mm A")}
                                </p>
                                <p className="mb-0">
                                    <span className="text-muted">Updated: </span>
                                    {moment(suggestion?.updatedAt).format("DD/MM/YYYY hh:mm A")}
                                </p>
                            </Col>
                        </Row>

                        {/* Location */}
                        {suggestion?.location?.length === 2 && (
                            <div className="mb-2">
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip style={{ position: "fixed" }}>View on Map</Tooltip>}
                                >
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${suggestion.location[0]},${suggestion.location[1]}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-secondary"
                                    >
                                        <i className="fas fa-map-marker-alt me-1"></i>
                                        View Location
                                    </a>
                                </OverlayTrigger>
                            </div>
                        )}

                        {/* Note */}
                        {suggestion?.note && (
                            <div
                                className="mt-2 p-3 rounded"
                                style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6" }}
                            >
                                <h6 className="text-muted mb-1">Note</h6>
                                <p className="mb-0">{suggestion.note}</p>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>

            {/* Items Table */}
            <Col lg="12">
                <Card>
                    <Card.Header>
                        <h4 className="card-title">Suggestion Items</h4>
                    </Card.Header>
                    <Card.Body>
                        <DataTable
                            columns={itemsColumns}
                            data={suggestion?.suggestionItems || []}
                            noDataComponent={
                                <div className="p-3 text-muted">No items in this suggestion.</div>
                            }
                        />
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
}
